import React from "react";
import { View } from "react-native";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Icon, Link, Text, useResponsive } from "@clearabee/ui-library-native";
import { ApiResponseData, instance, useCatalogue } from "@clearabee/ui-sdk";
import { Anchor } from "components/Core/Anchor/Anchor";
import type { AnchorProps } from "components/Core/Anchor/Anchor.types";
import { StepName } from "components/OrderTrain/OrderTrain";
import { convertApiStatus, getCategoryFromItems } from "lib/utils";
import { FeaturedJobPill } from "components/FeaturedJobPill/FeaturedJobPill";
import { styles } from "./UpcomingJobPill.styles";

interface UpcomingJobPillProps {
  job: ApiResponseData<typeof instance.jobs.postQueryJobs>["items"][0];
}

export const UpcomingJobPill = ({
  job,
}: UpcomingJobPillProps): React.ReactElement => {
  const [translate] = useTranslation();
  const catalogue = useCatalogue();
  const { screens } = useResponsive();

  const getDateCountdown = (): string | undefined => {
    if (!job) return;
    const diff = dayjs().startOf("day").diff(job.date, "day") * -1;

    if (diff <= 0) {
      return translate("hwa.screens.dashboard.userDashboard.arrivingToday");
    } else if (diff === 1) {
      return translate("hwa.screens.dashboard.userDashboard.arrivingTomorrow");
    } else {
      return `${diff} ${translate(
        "hwa.screens.dashboard.userDashboard.daysToGo",
      )}`;
    }
  };

  const ConditionalAnchor = (props: AnchorProps) => {
    if (screens.medium) {
      return <>{props.children}</>;
    }

    return <Anchor {...props} />;
  };

  return (
    <ConditionalAnchor routeName="Job" params={{ ref: job.ref }}>
      <FeaturedJobPill
        countdown={getDateCountdown() ?? ""}
        subheading={translate(
          "hwa.screens.dashboard.userDashboard.upcomingCollection",
        )}
        service={
          getCategoryFromItems(catalogue.defaultCatalogue, job.items ?? [])
            ?.title ?? ""
        }
        date={job.date}
        timeslot={
          <>
            {job.timePreference === "Any" &&
              translate(
                "hwa.screens.dashboard.userDashboard.timeslotPlaceholder",
              )}
            {job.timePreference !== "Any" &&
              job.timePreference !== "" &&
              job.timePreference}
            {job.timeslotJson &&
              `${dayjs(`2020-01-01T${job.timeslotJson.from}`).format(
                "ha",
              )} - ${dayjs(`2020-01-01T${job.timeslotJson.to}`).format("ha")}`}
          </>
        }
        step={convertApiStatus(job.status) as StepName}
      >
        <View style={[styles.activeJobDetails, { marginLeft: "auto" }]}>
          <View style={{ flexDirection: "row", marginLeft: "auto" }}>
            {screens.medium && (
              <Link underline>
                <Anchor routeName="Job" params={{ ref: job.ref }}>
                  <Text color="brand" fontSize="small">
                    {translate("common.viewJob")}
                  </Text>
                </Anchor>
              </Link>
            )}
            {!screens.medium && (
              <>
                <Text>{convertApiStatus(job.status)}</Text>
                <Icon.Chevron size="medium" color="brand" />
              </>
            )}
          </View>
        </View>
      </FeaturedJobPill>
    </ConditionalAnchor>
  );
};
